import { useState } from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { Dialog, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import QrCode2Icon from "@mui/icons-material/QrCode2";
import QRCode from "react-qr-code";

import { frameTransparentBackground } from "components/common/backgroundFrame";
import { getSessionJoinUrl } from "services/urlService";

import type { ShareQRCodeButtonProps } from "types/shareType";

const QR_CODE_SIZE = 240;

const ShareQRCodeButton = ({
    codeOnline,
    children,
}: ShareQRCodeButtonProps) => {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);

    function handleOpen() {
        setIsOpen(true);
    }

    function handleClose() {
        setIsOpen(false);
    }

    const joinUrl = getSessionJoinUrl(codeOnline);

    return (
        <>
            <SessionPillButton
                type="button"
                aria-label={t("shareQRCode.buttonLabel")}
                title={t("shareQRCode.buttonLabel")}
                onClick={handleOpen}
            >
                {children}
                <QrCode2Icon aria-hidden="true" />
            </SessionPillButton>

            <StyledDialog
                open={isOpen}
                onClose={handleClose}
                aria-label={t("shareQRCode.title")}
            >
                <CloseButton
                    aria-label={t("close")}
                    onClick={handleClose}
                    size="small"
                >
                    <CloseIcon />
                </CloseButton>
                <QRCodeWrapper>
                    <QRCode
                        value={joinUrl}
                        size={QR_CODE_SIZE}
                        role="img"
                        aria-label={t("shareQRCode.imageAlt", {
                            codeOnline,
                        })}
                    />
                </QRCodeWrapper>
            </StyledDialog>
        </>
    );
};

const SessionPillButton = styled.button`
    display: flex;
    align-items: center;
    gap: 0.4rem;
    height: 1.75rem;
    padding: 0 0.5rem 0 0.4375rem;
    border: 1px solid #ccc;
    border-radius: 1rem;
    background-color: white;
    color: #333;
    font: inherit;
    cursor: pointer;
    & .MuiSvgIcon-root {
        font-size: 1rem;
    }
    &:hover {
        filter: brightness(0.95);
    }
    /* Inside the footer frame: shares its background instead of an opaque white one. */
    ${frameTransparentBackground}
`;

// MUI's default 32px margin is too wide for the QR code on phones
const StyledDialog = styled(Dialog)`
    & .MuiDialog-paper {
        margin: 1rem;
    }
`;

// Absolute so it does not push the QR code off-center
const CloseButton = styled(IconButton)`
    &.MuiIconButton-root {
        position: absolute;
        top: 0.2rem;
        right: 0.2rem;
    }
`;

// Padding = scan quiet zone, wider than the close button so it never hides a corner
const QRCodeWrapper = styled.div`
    display: flex;
    justify-content: center;
    padding: 3.5rem 2.5rem 2.5rem;
    > svg {
        width: 100%;
        max-width: ${QR_CODE_SIZE}px;
        height: auto;
    }
`;

export default ShareQRCodeButton;
